/**
 * Member routes of both interfaces and the redirects between them
 * (plan-implementacion.md §1 "Rutas", slice F8).
 *
 * The classic app keeps most member surfaces on the org root, selected by
 * query params (`?tab=directory|community|messages`, `?sub=chapters|events|
 * mentorship|opportunities|travelers`, `?notifications=open`). Encuentro gives
 * each destination a real route. Every entry URL — including the ones already
 * sitting in emails, push payloads and bookmarks — must land on a valid screen
 * in both modes, so:
 *
 * - in 'encuentro', the old URLs redirect to the new routes;
 * - in 'classic', the new routes redirect to the old URLs.
 *
 * Both directions are 307 until the final cleanup (docs/redesign/README.md §6):
 * a cached 308 would outlive a rollback to classic and bounce between the two
 * mappings. Viajes is deprecated in Encuentro: its URLs land on People, and
 * the `city` filter (and any other query) rides along.
 *
 * This file is the only registry of entry routes (F8 owns it; screens do not
 * edit it). Pure and framework-free: the layout, the pages, the deep-link
 * allowlist and the frame all share it.
 */

import { INVITE_LINK_PARAM, isInviteLinkValue } from '@/lib/referrals/invite-link'
import type { UiVersion } from '@/lib/ui-variant/resolve'

/** The five destinations of the Encuentro frame. */
export const DESTINATIONS = ['home', 'people', 'groups', 'events', 'career'] as const
export type Destination = (typeof DESTINATIONS)[number]

export type PeopleView = 'map' | 'list'
export type CareerView = 'jobs' | 'mentoring'

/** Anything accepted as a query: a string (with or without '?'), URLSearchParams, or Next's searchParams record. */
export type SearchInput =
  | string
  | URLSearchParams
  | Record<string, string | string[] | undefined>
  | null
  | undefined

/** Normalize every accepted query shape to a fresh URLSearchParams. */
export function toSearchParams(input: SearchInput): URLSearchParams {
  if (!input) return new URLSearchParams()
  if (typeof input === 'string') return new URLSearchParams(input)
  if (input instanceof URLSearchParams) return new URLSearchParams(input)
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(input)) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v))
    else if (value !== undefined) params.append(key, value)
  }
  return params
}

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Routing keys of the classic root; every other param rides along on a redirect. */
const CLASSIC_ROUTING_KEYS = ['tab', 'sub', 'notifications'] as const

/** `?sub=` values the classic dashboard honors. */
type ClassicSub = 'chapters' | 'events' | 'mentorship' | 'opportunities' | 'travelers'
const CLASSIC_SUBS: readonly ClassicSub[] = [
  'chapters',
  'events',
  'mentorship',
  'opportunities',
  'travelers',
]

function normalize(value: string | null): string | null {
  const trimmed = value?.trim().toLowerCase()
  return trimmed ? trimmed : null
}

function parseClassicSub(value: string | null): ClassicSub | null {
  const sub = normalize(value)
  return sub && (CLASSIC_SUBS as readonly string[]).includes(sub) ? (sub as ClassicSub) : null
}

/** Classic `?view=` (map | directory | table) → People view. */
function peopleViewFromClassic(value: string | null): PeopleView | null {
  switch (normalize(value)) {
    case 'map':
      return 'map'
    case 'directory':
    case 'table':
    case 'list':
      return 'list'
    default:
      return null
  }
}

/** People view → classic `?view=`. */
function classicViewFromPeople(value: string | null): 'map' | 'directory' | null {
  switch (normalize(value)) {
    case 'map':
      return 'map'
    case 'list':
    case 'directory':
    case 'table':
      return 'directory'
    default:
      return null
  }
}

function parseCareerView(value: string | null): CareerView {
  return normalize(value) === 'mentoring' ? 'mentoring' : 'jobs'
}

/** Split `/in/:org/...` into its org slug and the segments after it. Null outside the member area. */
export function parseMemberPath(pathname: string): { org: string; rest: string[] } | null {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] !== 'in' || !segments[1]) return null
  return { org: segments[1], rest: segments.slice(2) }
}

// ---------------------------------------------------------------------------
// Canonical hrefs
// ---------------------------------------------------------------------------

/** The canonical href of a destination in a given interface. */
export function destinationHref(orgSlug: string, destination: Destination, version: UiVersion) {
  const base = `/in/${orgSlug}`
  if (version === 'encuentro') return destination === 'home' ? base : `${base}/${destination}`
  switch (destination) {
    case 'home':
      return base
    case 'people':
      return `${base}?tab=directory`
    case 'groups':
      return `${base}?tab=community&sub=chapters`
    case 'events':
      return `${base}?tab=community&sub=events`
    case 'career':
      return `${base}/jobs`
  }
}

/**
 * Destinations that mails and push link to. They are Encuentro routes, so a
 * link built with them needs no redirect (routes.test.ts and deep-link.test.ts
 * pin that); Classic is only reached through `toClassicHref`.
 */

/** Career on the mentoring view: the seeker's asks, where "¿Te ayudó?" lives. */
export function mentoringHref(orgSlug: string): string {
  return `/in/${orgSlug}/career?view=mentoring`
}

/** The notifications screen (the pending connection requests live here). */
export function notificationsHref(orgSlug: string): string {
  return `/in/${orgSlug}/notifications`
}

// ---------------------------------------------------------------------------
// Classic → Encuentro
// ---------------------------------------------------------------------------

/** The query of a classic root URL minus its routing keys (and `view`, which only People understands). */
function carriedFromClassicRoot(params: URLSearchParams): URLSearchParams {
  const carried = new URLSearchParams(params)
  for (const key of CLASSIC_ROUTING_KEYS) carried.delete(key)
  carried.delete('view')
  return carried
}

function encuentroRootTarget(org: string, params: URLSearchParams): string | null {
  const base = `/in/${org}`

  if (params.has('notifications')) {
    return withQuery(`${base}/notifications`, carriedFromClassicRoot(params))
  }

  // The welcome email's "Invitar a alguien" before SW-7 (`/in/<org>?invite=true`):
  // Personas opens the invite dialog (lib/referrals/invite-link.ts).
  if (isInviteLinkValue(params.get(INVITE_LINK_PARAM))) {
    return withQuery(`${base}/people`, carriedFromClassicRoot(params))
  }

  // A known `?sub=` wins over `?tab=`, as in the classic dashboard.
  const sub = parseClassicSub(params.get('sub'))
  const tab = normalize(params.get('tab'))
  const community = sub ?? (tab === 'community' ? 'chapters' : null)

  switch (community) {
    case 'chapters':
      return withQuery(`${base}/groups`, carriedFromClassicRoot(params))
    case 'events':
      return withQuery(`${base}/events`, carriedFromClassicRoot(params))
    case 'mentorship': {
      const carried = carriedFromClassicRoot(params)
      carried.set('view', 'mentoring')
      return withQuery(`${base}/career`, carried)
    }
    case 'opportunities':
      return withQuery(`${base}/career`, carriedFromClassicRoot(params))
    case 'travelers':
      // Viajes is deprecated: land on People; `city` (if any) rides along.
      return withQuery(`${base}/people`, carriedFromClassicRoot(params))
  }

  switch (tab) {
    case 'directory':
    case 'network': {
      const carried = carriedFromClassicRoot(params)
      const view = peopleViewFromClassic(params.get('view'))
      if (view) carried.set('view', view)
      return withQuery(`${base}/people`, carried)
    }
    case 'messages':
      return withQuery(`${base}/messages`, carriedFromClassicRoot(params))
    default:
      // Home (`?tab=home`, `feed`, unknown values) is the same URL. It does
      // not redirect: the Home ignores `tab`, and a client-side redirect
      // would drop the `#post-…` anchor that push links carry.
      return null
  }
}

/**
 * The member routes that exist only in Classic. Their URLs sit in mails and
 * push already delivered (CL-17), so they redirect for good: the proxy does it
 * (proxy.ts `legacyMemberRedirect`) and, for `opportunities/<id>`, which needs
 * the database, a route handler.
 */
export const LEGACY_SECTIONS = ['jobs', 'mentorship', 'chapters', 'opportunities'] as const

/**
 * Where a classic URL lives in Encuentro. Null when the URL is already an
 * Encuentro route, is shared by both (details, messages, profile, admin…)
 * or is not a member URL.
 */
export function toEncuentroHref(pathname: string, search?: SearchInput): string | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const { org, rest } = parsed
  const params = toSearchParams(search)
  const base = `/in/${org}`

  // The classic profile sheet (`?memberId=`) does not exist in Encuentro: the
  // person opens on their own page (S2), from the org root and from the four
  // routes that only exist in Classic (CL-17: Encuentro routes keep their own
  // `memberId` meaning, if any). The rest of the query rides along; the
  // classic routing keys don't.
  const memberId = params.get('memberId')
  if (
    memberId &&
    UUID_PATTERN.test(memberId) &&
    (rest.length === 0 || (LEGACY_SECTIONS as readonly string[]).includes(rest[0]))
  ) {
    const carried = carriedFromClassicRoot(params)
    carried.delete('memberId')
    return withQuery(`${base}/profile/${memberId.toLowerCase()}`, carried)
  }

  if (rest.length === 0) return encuentroRootTarget(org, params)

  const [section, id, ...deeper] = rest
  if (deeper.length > 0) return null

  switch (section) {
    case 'jobs': {
      if (id) return null // the job detail is shared
      const carried = new URLSearchParams(params)
      carried.delete('view')
      return withQuery(`${base}/career`, carried)
    }
    case 'mentorship': {
      if (id) return null
      const carried = new URLSearchParams(params)
      carried.set('view', 'mentoring')
      return withQuery(`${base}/career`, carried)
    }
    case 'chapters':
      return withQuery(id ? `${base}/groups/${id}` : `${base}/groups`, params)
    case 'opportunities': {
      // The board went to Career in one hop. `opportunities/<id>` needs the
      // database (jobs.legacy_opportunity_id): its route handler resolves it.
      if (id) return null
      const carried = new URLSearchParams(params)
      carried.delete('view')
      return withQuery(`${base}/career`, carried)
    }
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Encuentro → Classic
// ---------------------------------------------------------------------------

/**
 * Where an Encuentro route lives in the classic app. Null when the URL is
 * already classic, shared by both, or not a member URL.
 */
export function toClassicHref(pathname: string, search?: SearchInput): string | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const { org, rest } = parsed
  const params = toSearchParams(search)
  const base = `/in/${org}`
  const [section, id, ...deeper] = rest
  if (!section || deeper.length > 0) return null

  const rootWith = (routing: Record<string, string>, carried: URLSearchParams) => {
    const query = new URLSearchParams(routing)
    carried.forEach((value, key) => query.append(key, value))
    return withQuery(base, query)
  }
  const withoutView = () => {
    const carried = new URLSearchParams(params)
    carried.delete('view')
    return carried
  }

  switch (section) {
    case 'people': {
      if (id) return null
      const carried = withoutView()
      const view = classicViewFromPeople(params.get('view'))
      return rootWith(view ? { tab: 'directory', view } : { tab: 'directory' }, carried)
    }
    case 'groups':
      return id
        ? withQuery(`${base}/chapters/${id}`, params)
        : rootWith({ tab: 'community', sub: 'chapters' }, params)
    case 'chapters':
      // No classic index page: the Groups list lives on the root.
      return id ? null : rootWith({ tab: 'community', sub: 'chapters' }, params)
    case 'mentorship':
      // Mentoring has no classic route of its own: it is a community sub-view.
      return id ? null : rootWith({ tab: 'community', sub: 'mentorship' }, params)
    case 'events':
      return id ? null : rootWith({ tab: 'community', sub: 'events' }, params)
    case 'career': {
      if (id) return null
      return parseCareerView(params.get('view')) === 'mentoring'
        ? rootWith({ tab: 'community', sub: 'mentorship' }, withoutView())
        : withQuery(`${base}/jobs`, withoutView())
    }
    case 'notifications':
      return id ? null : rootWith({ notifications: 'open' }, params)
    default:
      return null
  }
}

/**
 * The redirect a request needs for the interface it renders, or null when
 * the URL already belongs to it. Callers issue it as a 307 (`redirect()`).
 */
export function resolveRouteRedirect(
  version: UiVersion,
  pathname: string,
  search?: SearchInput
): string | null {
  return version === 'encuentro'
    ? toEncuentroHref(pathname, search)
    : toClassicHref(pathname, search)
}

// ---------------------------------------------------------------------------
// Destinations of a path (active tab, list parents)
// ---------------------------------------------------------------------------

const SECTION_DESTINATION: Readonly<Record<string, Destination>> = {
  people: 'people',
  profile: 'people',
  groups: 'groups',
  chapters: 'groups',
  events: 'events',
  career: 'career',
  jobs: 'career',
  mentorship: 'career',
  opportunities: 'career',
  p: 'home',
}

/**
 * The destination a member URL belongs to, in either interface (so a detail
 * page or an old link still lights the right tab). Null for Messages,
 * Notifications, admin, help and anything outside `/in/:org`.
 */
export function destinationForPath(pathname: string, search?: SearchInput): Destination | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const [section] = parsed.rest
  if (section) return SECTION_DESTINATION[section] ?? null

  const target = encuentroRootTarget(parsed.org, toSearchParams(search))
  if (!target) return 'home'
  const targetSection = parseMemberPath(target.split('?')[0])?.rest[0]
  return targetSection ? (SECTION_DESTINATION[targetSection] ?? null) : 'home'
}

// ---------------------------------------------------------------------------
// Entry routes (deep-link allowlist)
// ---------------------------------------------------------------------------

/**
 * Graduation e-mail links (confirm, undo, and the pages they end on). Route
 * handlers that render their own interstitial: the native shell opens them with
 * a full page load (isDocumentRoute), and they are entry routes too.
 */
const DOCUMENT_ROUTE_PATTERNS: readonly RegExp[] = [
  /^\/grad\/(expired|undone)\/?$/,
  /^\/grad\/undo\/[^/]+\/?$/,
  /^\/grad\/[^/]+\/?$/,
]

/**
 * Every in-app path a Universal Link / App Link may open. Both interfaces'
 * routes are listed: an old link redirects once it lands.
 */
export const ENTRY_ROUTE_PATTERNS: readonly RegExp[] = [
  // Member area: Home and the five destinations (both interfaces)
  /^\/in\/[^/]+\/?$/,
  // `profile` alone is the member's own profile, `whats-new` the Novedades archive.
  /^\/in\/[^/]+\/(people|groups|events|career|notifications|jobs|mentorship|chapters|opportunities|profile|whats-new)\/?$/,
  // Details
  /^\/in\/[^/]+\/(groups|chapters|events|jobs|opportunities|profile|p)\/[^/]+\/?$/,
  // Nested detail screens: profile edit / LinkedIn / mutual, classmates, job edit
  /^\/in\/[^/]+\/profile\/[^/]+\/(linkedin|linkedin\/updates|mutual|edit\/[^/]+)\/?$/,
  /^\/in\/[^/]+\/groups\/[^/]+\/classmates\/?$/,
  /^\/in\/[^/]+\/jobs\/[^/]+\/edit\/?$/,
  // Career flows: post a job, ask for mentoring
  /^\/in\/[^/]+\/career\/(jobs\/new|mentoring\/ask)\/?$/,
  // Messages
  /^\/in\/[^/]+\/messages(\/[^/]+)?\/?$/,
  // Help: index, category, article
  /^\/in\/[^/]+\/help(\/[^/]+){0,2}\/?$/,
  // Admin (mails and pushes open members, events, chapters, billing, setup…;
  // the deepest screen is chapters/:id/events/:id/edit)
  /^\/in\/[^/]+\/admin(\/[^/]+){0,5}\/?$/,
  // Account and auth
  /^\/requests\/?$/,
  /^\/settings(\/|$)/,
  /^\/(login|signup)(\/|$)/,
  /^\/profile(\/[^/]+)?\/?$/,
  // Entry flows the site claims as app links (AASA claims every path but /auth/*,
  // the Android manifest lists these prefixes): an invitation email opens
  // /join?token=… or /join/<token>, and was dropped here, so the app stayed on the last
  // screen and the invitation was never accepted. /auth/* stays in the browser on purpose.
  /^\/join(\/[^/]+)?\/?$/,
  /^\/(onboarding|start|setup|forgot-password)(\/|$)/,
  /^\/payment(\/|$)/,
  // Graduation e-mail links (confirm, undo and the pages they end on). They are
  // route handlers that render their own interstitial, so the native shell
  // opens them with a full page load (see isDocumentRoute).
  ...DOCUMENT_ROUTE_PATTERNS,
]

/** Entry routes served by a route handler (server-built HTML): loaded like a browser would. */
export function isDocumentRoute(pathname: string): boolean {
  return DOCUMENT_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
}

export function isEntryRoute(pathname: string): boolean {
  return ENTRY_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
}
